import React, { PropsWithChildren } from 'react';
import Blogroll from './blocks/Blogroll';
import CommunityBanner from './blocks/CommunityBanner';
import ContactBanner from './blocks/ContactBanner';
import Faqs from './blocks/Faqs';
import FranchiseDisclosure from './blocks/FranchiseDisclosure';
import Gallery from './blocks/Gallery';
import Hero from './blocks/Hero';
import IconBullets from './blocks/IconBullets';
import IngredientsDisclosure from './blocks/IngredientsDisclosure';
import InsurerGrid from './blocks/InsurerGrid';
import MediaContent from './blocks/MediaContent';
import MediaContentFaqs from './blocks/MediacontentFaqs';
import FaqsGrid from './blocks/FaqsGrid';
import MetroDetail from './blocks/MetroDetail';
import MultiBlogroll from './blocks/MultiBlogroll';
import PartnerLogos from './blocks/PartnerLogos';
import Quicklinks from './blocks/Quicklinks';
import ResourceDetail from './blocks/ResourceDetail';
import ReviewGrid from './blocks/ReviewGrid';
import RichTextBlock from './blocks/RichtextBlock';
import ServicesDetail from './blocks/ServicesDetail';
import ServicesSlider from './blocks/ServicesSlider';
import Slider from './blocks/Slider';
import SplitTextBanner from './blocks/SplitTextBanner';
import Subnav from './blocks/Subnav';
import TeamMembers from './blocks/team-members';
import Testimonial from './blocks/Testimonial';
import TextBanner from './blocks/TextBanner';
import Form from './blocks/form';
import Anchor from './blocks/Anchor';
import ServicesWeProvide from './blocks/ServicesWeProvide';
import ServiceArea from './blocks/ServiceArea';
import AboutFranchiseHero from './blocks/AboutFranchiseHero';
import FranchiseAboutInfo from './blocks/FranchiseAboutInfo';
import FranchiseContactHero from './blocks/FranchiseContactHero';
import FranchiseCollapsibleAbout from './blocks/FranchiseCollapsibleAbout';
import FranchiseTeam from './blocks/franchise-team';
import { useDebug } from './primitives/debug/debug-provider';
import DebugPill from './primitives/debug/debug-pill';
import EmptyBlock from './blocks/empty-block';
import Row from './primitives/grid/row';
import Col from './primitives/grid/col';
import Container from './primitives/grid/container';
import { breakpoints } from './primitives/tokens';
import SidebarProvider from '../contexts/sidebar-provider';
import FranchiseFormHero from './blocks/franchise-form-hero';
import FranchiseFormCta from './blocks/franchise-form-cta';
/*
* Context for retaining render depth
* */
export const BlockRendererContext = React.createContext(-1);

type BlockWithSize = (data: any) => ((JSX.Element | null) & { worksWithSidebar?: boolean });
type ComponentResolution = (Component: (data: any) => JSX.Element|null, data: any, blockContext: any, pageData: any) => JSX.Element;

const resolveOld: ComponentResolution = (Component, data, blockContext, pageData) => <Component data={data} blockContext={blockContext} pageData={pageData} />;

const resolveNew: ComponentResolution = (Component, data) => <Component {...data} />;

const getBlock = (name: string): [BlockWithSize | null, ComponentResolution | null] => {
    switch (name) {
        case 'franchise_form_cta':
            return [FranchiseFormCta, resolveNew];
        case 'franchise_form_hero':
            return [FranchiseFormHero, resolveNew];
        case 'blogroll':
            return [Blogroll, resolveOld];
        case 'community_banner':
            return [CommunityBanner, resolveOld];
        case 'contact_banner':
            return [ContactBanner, resolveOld];
        case 'faqs':
            return [Faqs, resolveOld];
        case 'franchise_disclosure':
            return [FranchiseDisclosure, resolveOld];
        case 'gallery':
            return [Gallery, resolveOld];
        case 'hero':
            return [Hero, resolveOld];
        case 'icon_bullets':
            return [IconBullets, resolveOld];
        case 'ingredients_disclosure':
            return [IngredientsDisclosure, resolveOld];
        case 'insurer_grid':
            return [InsurerGrid, resolveOld];
        case 'media_content':
            return [MediaContent, resolveOld];
        case 'mediacontent_faqs':
            return [MediaContentFaqs, resolveOld];
        case 'faqs_grid':
            return [FaqsGrid, resolveOld];
        case 'metro_detail':
            return [MetroDetail, resolveOld];
        case 'multi_blogroll':
            return [MultiBlogroll, resolveOld];
        case 'partner_logos':
            return [PartnerLogos, resolveOld];
        case 'quicklinks':
            return [Quicklinks, resolveOld];
        case 'resource_detail':
            return [ResourceDetail, resolveOld];
        case 'review_grid':
            return [ReviewGrid, resolveOld];
        case 'richtext_block':
            return [RichTextBlock, resolveOld];
        case 'services_detail':
            return [ServicesDetail, resolveOld];
        case 'services_slider':
            return [ServicesSlider, resolveOld];
        case 'slider':
            return [Slider, resolveOld];
        case 'split_text_banner':
            return [SplitTextBanner, resolveOld];
        case 'subnav_stripe':
            return [Subnav, resolveOld];
        case 'team_member_grid':
            return [TeamMembers, resolveOld];
        case 'testimonial':
            return [Testimonial, resolveOld];
        case 'text_banner':
            return [TextBanner, resolveOld];
        case 'form':
            return [Form, resolveOld];
        case 'anchor':
            return [Anchor, resolveOld];
        case 'services_we_provide':
            return [ServicesWeProvide, resolveOld];
        case 'service_area':
            return [ServiceArea, resolveOld];
        case 'about_franchise_hero':
            return [AboutFranchiseHero, resolveOld];
        case 'franchise_details':
            return [FranchiseAboutInfo, resolveOld];
        case 'franchise_contact_hero':
            return [FranchiseContactHero, resolveOld];
        case 'franchise_collapsible_about':
            return [FranchiseCollapsibleAbout, resolveOld];
        case 'franchise_about_info':
            return [FranchiseAboutInfo, resolveOld];
        case 'franchise_team_members':
            return [FranchiseTeam, resolveNew];
        default:
            return [null, null];
    }
};

interface BlockRendererProps {
    data?: Queries.CmsPageBuilderBlocks[]
    blockContext?: any // Deprecated, use contexts
    pageData?: any // Deprecated, use contexts
}

enum BlockStatus {
    Error,
    Found,
}

interface BlockDataReturn {
    status: BlockStatus,
    name: string|null
    block?: [BlockWithSize | null, ComponentResolution | null]
    data?: any
    worksWithSidebar?: boolean
}

interface RenderSetOfBlocksProps {
    blocks: BlockDataReturn[]
    blockContext?: any // Deprecated, use contexts
    pageData?: any // Deprecated, use contexts
}

const RenderSetOfBlocks = ({ blocks, blockContext, pageData }: RenderSetOfBlocksProps): JSX.Element => {
    const { showBlockNames } = useDebug();
    return (
        <>
            {blocks.map((block, idx) => {
                if (!block.name) {
                    if (process.env.NODE_ENV === 'development') {
                        return (
                            <EmptyBlock>
                                Unable to find any block for data point.
                            </EmptyBlock>
                        );
                    }
                    return null;
                }

                if (!block.block || !block.block[0] || !block.block[1]) {
                    if (process.env.NODE_ENV === 'development') {
                        return (
                            <EmptyBlock>
                                Unable to find block
                                {' '}
                                {block.name}
                                .
                            </EmptyBlock>
                        );
                    }
                    return null;
                }

                const BlockJsx = block.block[1](
                    block.block[0],
                    block.data,
                    blockContext,
                    pageData,
                );

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={idx.toString()}>
                        {showBlockNames && (
                            <DebugPill css={{
                                position: 'absolute',
                            }}
                            >
                                {block.name as string}
                            </DebugPill>
                        )}
                        {BlockJsx}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const BlockRenderer = ({ data, blockContext, pageData, children }: PropsWithChildren<BlockRendererProps>): JSX.Element => {
    const existingRenderContextDepth = React.useContext(BlockRendererContext);

    const blockItems: BlockDataReturn[] = (data || []).map((block, idx) => {
        const blockName = Object
            .keys(block)
            .find(key => block[key as keyof Queries.CmsPageBuilderBlocks] !== null);

        if (!blockName) {
            return {
                status: BlockStatus.Error,
                name: null,
            };
        }

        const matchedBlock = getBlock(blockName);

        if (!matchedBlock || !matchedBlock[0] || !matchedBlock[1]) {
            return {
                status: BlockStatus.Error,
                name: blockName,
            };
        }

        return {
            status: BlockStatus.Found,
            name: blockName,
            block: matchedBlock,
            data: block[blockName as keyof Queries.CmsPageBuilderBlocks],
            worksWithSidebar: matchedBlock[0]?.worksWithSidebar,
        };
    });

    let preSidebar: BlockDataReturn[] = [];
    const duringSidebar: BlockDataReturn[] = [];
    const postSidebar: BlockDataReturn[] = [];

    const hasChildren = !!children;

    if (hasChildren) {
        let sidebarTriggered = false;
        let sidebarCompleted = false;
        for (let i = 0; i < blockItems.length - 1; i++) {
            const cur = blockItems[i];
            if (cur.worksWithSidebar && !sidebarCompleted) {
                sidebarTriggered = true;
                duringSidebar.push(cur);
            } else if (!cur.worksWithSidebar && sidebarTriggered) {
                sidebarCompleted = true;
                postSidebar.push(cur);
            } else if (!cur.worksWithSidebar && !sidebarTriggered) {
                preSidebar.push(cur);
            } else {
                postSidebar.push(cur);
            }
        }
    } else {
        preSidebar = blockItems;
    }

    return (
        <BlockRendererContext.Provider value={existingRenderContextDepth + 1}>
            {preSidebar.length > 0 && (
                <RenderSetOfBlocks blocks={preSidebar} blockContext={blockContext} pageData={pageData} />
            )}
            {duringSidebar.length > 0 && (
                <SidebarProvider>
                    <Container>
                        <Row>
                            <Col breakpoints={{
                                dt: { span: 4, start: 8 },
                                tb: { span: 12, start: 0 },
                            }}
                            >
                                <aside css={{
                                    position: 'sticky',
                                    top: '180px',
                                    paddingBottom: '24px',
                                    [breakpoints.tb]: {
                                        paddingBottom: '32px',
                                    },
                                }}
                                >
                                    {children}
                                </aside>
                            </Col>
                            <Col breakpoints={{
                                dt: { span: 7 },
                                tb: { span: 12, start: 0 },
                            }}
                            >
                                <RenderSetOfBlocks blocks={duringSidebar} blockContext={blockContext} pageData={pageData} />
                            </Col>
                        </Row>
                    </Container>
                </SidebarProvider>
            )}
            {postSidebar.length > 0 && (
                <RenderSetOfBlocks blocks={postSidebar} blockContext={blockContext} pageData={pageData} />
            )}
        </BlockRendererContext.Provider>
    );
};

export default BlockRenderer;
